import { useEffect, useState, useCallback } from 'react';
import { useGetLegalAcceptanceStatusQuery } from '../services/nestjs-base-api';

interface UseLegalAcceptanceOptions {
    jwtToken: string | null;
    skip?: boolean;
}

interface UseLegalAcceptanceResult {
    needsAcceptance: boolean;
    pendingDocuments: string[];
    isLoading: boolean;
    isError: boolean;
    isEnabled: boolean;
    refetch: () => void;
    markAsAccepted: () => void;
}

/**
 * Hook to check if the current user needs to accept legal documents
 *
 * Usage:
 * ```tsx
 * const { needsAcceptance, pendingDocuments, isLoading, markAsAccepted } = useLegalAcceptance({
 *     jwtToken: user?.jwtToken || null,
 * });
 *
 * // Show modal if needed
 * {needsAcceptance && (
 *     <LegalAcceptanceModal
 *         visible={needsAcceptance}
 *         onAccepted={markAsAccepted}
 *         pendingDocuments={pendingDocuments}
 *         jwtToken={jwtToken}
 *     />
 * )}
 * ```
 */
export function useLegalAcceptance({
    jwtToken,
    skip = false,
}: UseLegalAcceptanceOptions): UseLegalAcceptanceResult {
    const [hasAcceptedInSession, setHasAcceptedInSession] = useState(false);

    const { data, isLoading, isError, refetch } = useGetLegalAcceptanceStatusQuery(
        { jwtToken: jwtToken || '' },
        {
            skip: skip || !jwtToken || hasAcceptedInSession,
            refetchOnMountOrArgChange: true,
        }
    );

    // Reset session acceptance flag when user changes
    useEffect(() => {
        setHasAcceptedInSession(false);
    }, [jwtToken]);

    const markAsAccepted = useCallback(() => {
        setHasAcceptedInSession(true);
    }, []);

    // Only require acceptance if feature is enabled AND there are pending documents
    const needsAcceptance = !hasAcceptedInSession && !!data && data.enabled && !data.allAccepted;
    const pendingDocuments = data?.pendingDocuments || [];
    const isEnabled = data?.enabled ?? false;

    return {
        needsAcceptance,
        pendingDocuments,
        isLoading,
        isError,
        isEnabled,
        refetch,
        markAsAccepted,
    };
}

export default useLegalAcceptance;
